import React from 'react';
import PropTypes from 'prop-types';
import { View } from 'react-native-web';

import { Box } from '@styleseat/ui';
import SupportServiceWrapper from '../../../modules/SupportServiceWrapper';
import Icon from '../../ui/Icon';
import Button from '../../ui/Button';
import Modal from '../../Modal';
import { HeaderText } from '../../ui/HeaderText';
import { BodyText } from '../../ui';
import ICON_AUTOCHECKOUT from '../../../../images/icons/icon-autocheckout.svg';

import './AutoCheckoutModal.module.scss';

const ModalHeaderText = ({
  children,
  ...props
}) => (
  <HeaderText {...props} theme="provider" styleName="modalHeaderText">
    {children}
  </HeaderText>
);

const ModalBodyText = ({
  children,
  ...props
}) => (
  <BodyText {...props} theme="provider" styleName="modalBodyText">
    {children}
  </BodyText>
);

export const AutoCheckoutModalContent = ({
  providerId,
  autoCheckoutInfo,
  failAtCheckoutInfo,
  smsDisabledInfo,
}) => (
  <View styleName="modalContent">
    {/* show the auto-checkout help info */}
    {autoCheckoutInfo && (
      <View>
        <Box
          flexDirection="row"
          justifyContent="center"
          paddingBottom={24}
        >
          <Icon
            size={96}
            uri={ICON_AUTOCHECKOUT}
          />
        </Box>
        <ModalHeaderText>The card on file will be charged&nbsp;automatically</ModalHeaderText>
        <ModalBodyText>
          An automatic 20% tip will be included, or your client can add a custom tip from the text
          message we send after the&nbsp;appointment.
        </ModalBodyText>
        <ModalBodyText>
          If there’s a problem with the card on file, the appointment will appear red on
          your&nbsp;calendar.
        </ModalBodyText>
        <ModalBodyText>
          All edits, including total service cost, must be made within one hour of the appointment
          end&nbsp;time.
        </ModalBodyText>
        <Button
          text="Payment Settings"
          link="provider.settings_menu.payments"
          linkParams={{ providerId }}
          size="small"
          appearance="text-primary"
        />
      </View>
    )}

    {/* show the card declined at checkout failed state info */}
    {failAtCheckoutInfo && (
      <View>
        <ModalHeaderText>The card on file was declined.</ModalHeaderText>
        <ModalBodyText>
          Contact your client so you can update their payment method and check out manually.
        </ModalBodyText>
      </View>
    )}

    {/* show the client sms not enabled failed state info */}
    {smsDisabledInfo && (
      <View>
        <ModalHeaderText>
          Your client must turn on text notifications to be eligible for automatic checkout.
        </ModalHeaderText>
        <ModalBodyText>
          To charge your client for this appointment, update the payment method and check out
          manually.
        </ModalBodyText>
        <ModalBodyText>
          You can invite clients to turn on text notifications from <em>client details</em>.
        </ModalBodyText>
      </View>
    )}
  </View>
);

/**
 * This modal displays the auto-checkout info help modal
 */
export default function AutoCheckoutModal(props) {
  const {
    providerId,
    title,
    failAtCheckout,
    clientSmsDisabled,
    renderContainer,
  } = props;

  const autoCheckoutInfo = !failAtCheckout && !clientSmsDisabled;
  const failAtCheckoutInfo = failAtCheckout;
  const smsDisabledInfo = !failAtCheckout && clientSmsDisabled;

  return (
    <Modal
      title={title}
      initialOpen
      transitionOpen="slide-in-up"
      transitionClose="slide-out-down"
      className="auto-checkout-info-component"
      renderContainer={renderContainer}
    >
      <AutoCheckoutModalContent
        providerId={providerId}
        autoCheckoutInfo={autoCheckoutInfo}
        failAtCheckoutInfo={failAtCheckoutInfo}
        smsDisabledInfo={smsDisabledInfo}
      />
      <View styleName="questionsAndSupport">
        <HeaderText level="2" theme="provider">
          Questions?
        </HeaderText>
        {/* eslint-disable-next-line react/jsx-handler-names */}
        <Button onClick={SupportServiceWrapper.open} size="small" appearance="text-primary">
          Contact Support
        </Button>
      </View>
    </Modal>
  );
}

AutoCheckoutModal.propTypes = {
  providerId: PropTypes.number,
  title: PropTypes.string,
  failAtCheckout: PropTypes.bool,
  clientSmsDisabled: PropTypes.bool,
  renderContainer: PropTypes.object.isRequired,
};

AutoCheckoutModal.defaultProps = {
  providerId: 0,
  title: '',
  failAtCheckout: false,
  clientSmsDisabled: false,
};

AutoCheckoutModalContent.propTypes = {
  providerId: PropTypes.number.isRequired,
  autoCheckoutInfo: PropTypes.bool.isRequired,
  failAtCheckoutInfo: PropTypes.bool.isRequired,
  smsDisabledInfo: PropTypes.bool.isRequired,
};

ModalHeaderText.propTypes = {
  children: PropTypes.string.isRequired,
};

ModalBodyText.propTypes = {
  children: PropTypes.string.isRequired,
};
